import { motion } from "framer-motion";
import { Check, Phone, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { FINANCING } from "@/lib/data";

export const Financing = () => (
  <section id="financing" data-testid="financing-section" className="bg-bone py-28 lg:py-40">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
        <div className="lg:col-span-8">
          <Reveal>
            <p className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-olive">
              <span className="h-[1px] w-10 bg-olive" /> Financing
            </p>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-[var(--astera-char)]">
              Care that fits your life — and your <span className="italic text-clay">budget.</span>
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-4">
          <Reveal delay={0.1}>
            <p className="text-[var(--astera-char)]/65 leading-relaxed">
              We partner with trusted financing providers so you can begin your care now and
              pay over time. Apply in minutes — most decisions are instant.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {FINANCING.map((f, i) => (
          <Reveal key={f.name} delay={i * 0.1}>
            <div
              data-testid={`financing-card-${i}`}
              className="h-full flex flex-col rounded-[24px] border border-[#EAE6E1] bg-[var(--astera-olive-soft)]/40 p-8 lg:p-9"
            >
              <span className="font-serif text-2xl text-clay/40">0{i + 1}</span>
              <h3 className="mt-4 font-serif text-3xl text-[var(--astera-char)]">{f.name}</h3>
              <p className="mt-2 text-sm text-[var(--astera-char)]/60">{f.tagline}</p>

              <ul className="mt-6 space-y-3 border-t border-[#EAE6E1] pt-6 flex-1">
                {f.points.map((p, k) => (
                  <li key={k} className="flex items-start gap-3 text-sm text-[var(--astera-char)]/75">
                    <Check size={16} strokeWidth={1.6} className="mt-0.5 shrink-0 text-olive" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col gap-2.5">
                <motion.a
                  href={f.apply}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`financing-apply-${i}`}
                  className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-clay px-6 py-3.5 text-sm uppercase tracking-[0.14em] text-[var(--astera-bone)] transition-colors duration-300 hover:bg-[var(--astera-char)]"
                >
                  Apply online
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </motion.a>
                <a
                  href={`tel:${f.phone}`}
                  data-testid={`financing-call-${i}`}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--astera-char)]/20 px-6 py-3 text-sm tracking-wide text-[var(--astera-char)] transition-colors duration-300 hover:border-clay hover:text-clay"
                >
                  <Phone size={15} />
                  Or call · {f.phoneLabel}
                </a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <p className="mt-10 text-xs text-[var(--astera-char)]/45 max-w-3xl">
          Financing is provided by independent third-party lenders. Approval, rates and terms are
          determined by each provider and subject to their eligibility criteria.
        </p>
      </Reveal>
    </div>
  </section>
);
